import { getGuidanceSessionTypeDisplayName } from '@fruitful/guidance';
import {
    GuidanceSessionType,
    POST_SESSION_WORKFLOW_GENERAL_PROMPT_KEY,
    POST_SESSION_WORKFLOW_PROMPT_KEY,
} from '@fruitful/types';
import { useState } from 'react';
import {
    BodySmallEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    EditIcon,
    H2Emphasized,
    Picker,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { useSimulatePostSessionWorkflow } from '../../guidance-sessions/hooks/useSimulatePostSessionWorkflow';
import { captureException } from '../../logger/Logger';

const TRANSCRIPT_FIXTURE_OPTIONS = [
    { label: 'Select a Transcript', value: '' },
    { label: 'Transcript 1: Susie Berries', value: 'TRANSCRIPT_1' },
    { label: 'Transcript 2: Mary Mango', value: 'TRANSCRIPT_2' },
    { label: 'Transcript 3: Peter Pear', value: 'TRANSCRIPT_3' },
    { label: 'Transcript 4: Anakin', value: 'TRANSCRIPT_4' },
    { label: 'Transcript 5: Han', value: 'TRANSCRIPT_5' },
    { label: 'Transcript 6: Leia & Chewbacca', value: 'TRANSCRIPT_6' },
    { label: 'Transcript 7: Padme & Ani', value: 'TRANSCRIPT_7' },
    { label: 'Transcript 8: Rey', value: 'TRANSCRIPT_8' },
    { label: 'Transcript 9: Obi', value: 'TRANSCRIPT_9' },
    { label: 'Transcript 10: Qui-Gon', value: 'TRANSCRIPT_10' },
    { label: 'Transcript 11: Bethany Blueberry', value: 'TRANSCRIPT_11' },
    { label: 'Transcript 12: Carly Carrot', value: 'TRANSCRIPT_12' },
    { label: 'Transcript 13: Barbie & Ken', value: 'TRANSCRIPT_13' },
    { label: 'Transcript 14: Arwen', value: 'TRANSCRIPT_14' },
    { label: 'Transcript 15: Mango', value: 'TRANSCRIPT_15' },
    { label: 'Transcript 16: Venus', value: 'TRANSCRIPT_16' },
    { label: 'Transcript 17: Anna Kiwi', value: 'TRANSCRIPT_17' },
    {
        label: 'Transcript 18: Gloria Garden & Mister',
        value: 'TRANSCRIPT_18',
    },
    { label: 'Transcript 19: Sunny Day', value: 'TRANSCRIPT_19' },
    { label: 'Transcript 20: Maria Chen', value: 'TRANSCRIPT_20' },
    { label: 'Transcript 21: Robert Diaz', value: 'TRANSCRIPT_21' },
    { label: 'Transcript 22: Priya Patel', value: 'TRANSCRIPT_22' },
    { label: 'Transcript 23: Randy', value: 'TRANSCRIPT_23' },
    { label: 'Transcript 24: Mia Mango', value: 'TRANSCRIPT_24' },
    {
        label: 'Transcript 25: Strawberry Donuts',
        value: 'TRANSCRIPT_25',
    },
];

const SESSION_TYPE_OPTIONS = [
    { label: 'Default (from session)', value: '' },
    ...Object.values(GuidanceSessionType).map((type) => ({
        label: getGuidanceSessionTypeDisplayName(type, undefined),
        value: type,
    })),
];

const isGuidanceSessionType = (value: string): value is GuidanceSessionType =>
    (Object.values(GuidanceSessionType) as string[]).includes(value);

interface WorkflowSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onEditPrompt: (promptKey: string) => void;
    refetchWorkflowRuns?: () => Promise<void>;
}

const WorkflowSettingsModal = ({
    isOpen,
    onClose,
    onEditPrompt,
    refetchWorkflowRuns,
}: WorkflowSettingsModalProps) => {
    const [selectedSessionType, setSelectedSessionType] = useState('');
    const [selectedFixture, setSelectedFixture] = useState('');

    const toast = useToastController();
    const {
        mutateAsync: simulatePostSessionWorkflow,
        isPending: isSimulating,
    } = useSimulatePostSessionWorkflow();

    const handleSimulate = async () => {
        if (!selectedFixture) {
            return;
        }

        const sessionTypeOverride = isGuidanceSessionType(selectedSessionType)
            ? selectedSessionType
            : undefined;

        try {
            await simulatePostSessionWorkflow({
                transcriptFixture: selectedFixture,
                sessionTypeOverride,
            });
            toast.show(
                'Workflow simulation started!',
                MutationSuccessToastOptions,
            );
            setSelectedFixture('');
            setSelectedSessionType('');
            await refetchWorkflowRuns?.();
            onClose();
        } catch (error) {
            toast.show(
                'Failed to simulate transcript',
                MutationErrorToastOptions(error),
            );
            captureException(error);
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <YStack gap={24} padding={24} width={520}>
                <H2Emphasized>Settings</H2Emphasized>

                <YStack gap={8}>
                    <BodySmallEmphasized>Prompts</BodySmallEmphasized>
                    <XStack gap={12} flexWrap="wrap">
                        <Button
                            text="Edit Discovery Prompt"
                            onPress={() => {
                                onEditPrompt(POST_SESSION_WORKFLOW_PROMPT_KEY);
                            }}
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            icon={EditIcon}
                            paddingHorizontal={16}
                        />
                        <Button
                            text="Edit Non-Discovery Prompt"
                            onPress={() => {
                                onEditPrompt(
                                    POST_SESSION_WORKFLOW_GENERAL_PROMPT_KEY,
                                );
                            }}
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            icon={EditIcon}
                            paddingHorizontal={16}
                        />
                    </XStack>
                </YStack>

                <YStack gap={16}>
                    <YStack gap={8}>
                        <BodySmallEmphasized>Session Type</BodySmallEmphasized>
                        <Picker
                            size="regular"
                            value={selectedSessionType}
                            onChange={setSelectedSessionType}
                            options={SESSION_TYPE_OPTIONS}
                        />
                    </YStack>

                    <YStack gap={8}>
                        <BodySmallEmphasized>Transcript</BodySmallEmphasized>
                        <Picker
                            size="regular"
                            value={selectedFixture}
                            onChange={setSelectedFixture}
                            options={TRANSCRIPT_FIXTURE_OPTIONS}
                        />
                    </YStack>

                    <Button
                        text={
                            isSimulating
                                ? 'Simulating...'
                                : 'Simulate Transcript'
                        }
                        onPress={() => {
                            void handleSimulate();
                        }}
                        disabled={!selectedFixture || isSimulating}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        width="full"
                    />
                </YStack>
            </YStack>
        </GroveSimpleModal>
    );
};

export default WorkflowSettingsModal;
